/**
 * LinkedSourceRow / LinkedSourceSelector
 *
 * Reusable component for associating any HIPAA control record with a
 * knowledge source (for manual controls).
 *
 * Usage in any HIPAA control detail or create dialog:
 *   <LinkedSourceRow entityType="CspSecurityControl" entityId={control.id} canManage={canManage} />
 *
 * The component:
 * - Fetches the current linked source (if any) via GET /api/organization/compliance/hipaa/control-source-links
 * - Shows a badge with the source name + type when linked
 * - Has a "Link Source" button that opens a small selector dialog
 * - Saves via PUT /api/organization/compliance/hipaa/control-source-links
 * - Has an "Unlink" action when a link already exists
 */
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Link2, Unlink } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

function useHipaaFetch() {
  return (url: string, opts: RequestInit = {}) => {
    const token =
      sessionStorage.getItem("dotnetbot.jwt.session") ||
      localStorage.getItem("dotnetbot.jwt") ||
      "";
    return fetch(url, {
      ...opts,
      headers: {
        ...(opts.headers as Record<string, string> || {}),
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });
  };
}

interface AvailableSource {
  id: string;
  name: string;
  url: string | null;
  type: string;
  chatbotId: string;
  hipaaVerificationStatus: string | null;
}

interface ControlLink {
  entityType: string;
  entityId: string;
  knowledgeSourceId: string;
  sourceName: string;
  sourceUrl: string | null;
  sourceType: string;
  hipaaVerificationStatus: string | null;
  notes: string | null;
}

export function LinkedSourceRow({
  entityType,
  entityId,
  canManage = false,
}: {
  entityType: string;
  entityId: string;
  canManage?: boolean;
}) {
  const authFetch = useHipaaFetch();
  const qc = useQueryClient();
  const { toast } = useToast();
  const BASE = "/api/organization/compliance/hipaa";

  const [showPicker, setShowPicker] = useState(false);
  const [selectedSourceId, setSelectedSourceId] = useState("");
  const [notes, setNotes] = useState("");

  const linkQ = useQuery<ControlLink | null>({
    queryKey: ["hipaa-control-link", entityType, entityId],
    queryFn: async () => {
      const r = await authFetch(`${BASE}/control-source-links?entityType=${encodeURIComponent(entityType)}&entityId=${encodeURIComponent(entityId)}`);
      if (r.status === 404) return null;
      if (!r.ok) return null;
      return r.json();
    },
    staleTime: 30_000,
  });

  const sourcesQ = useQuery<AvailableSource[]>({
    queryKey: ["hipaa-available-sources"],
    queryFn: () => authFetch(`${BASE}/available-sources`).then(r => r.json()),
    enabled: showPicker,
    staleTime: 60_000,
  });

  const linkM = useMutation({
    mutationFn: () =>
      authFetch(`${BASE}/control-source-links`, {
        method: "PUT",
        body: JSON.stringify({ entityType, entityId, knowledgeSourceId: selectedSourceId, notes: notes || null }),
      }),
    onSuccess: () => {
      toast({ title: "Knowledge source linked." });
      qc.invalidateQueries({ queryKey: ["hipaa-control-link", entityType, entityId] });
      setShowPicker(false);
      setSelectedSourceId("");
      setNotes("");
    },
    onError: () => toast({ title: "Failed to link source.", variant: "destructive" }),
  });

  const unlinkM = useMutation({
    mutationFn: () =>
      authFetch(
        `${BASE}/control-source-links?entityType=${encodeURIComponent(entityType)}&entityId=${encodeURIComponent(entityId)}`,
        { method: "DELETE" },
      ),
    onSuccess: () => {
      toast({ title: "Source link removed." });
      qc.invalidateQueries({ queryKey: ["hipaa-control-link", entityType, entityId] });
    },
    onError: () => toast({ title: "Failed to unlink source.", variant: "destructive" }),
  });

  const link = linkQ.data;

  return (
    <div className="rounded border p-2 text-xs space-y-1">
      <div className="flex items-center justify-between gap-2">
        <p className="font-semibold flex items-center gap-1">
          <Link2 className="w-3 h-3" aria-hidden="true" />
          Linked Knowledge Source
        </p>
        {canManage && (
          <div className="flex items-center gap-1">
            <Button size="sm" variant="outline" className="h-6 text-xs px-2" onClick={() => {
              setSelectedSourceId(link?.knowledgeSourceId ?? "");
              setNotes(link?.notes ?? "");
              setShowPicker(true);
            }}>
              {link ? "Change" : "Link Source"}
            </Button>
            {link && (
              <Button size="sm" variant="ghost" className="h-6 text-xs px-2 text-destructive hover:text-destructive"
                onClick={() => unlinkM.mutate()} disabled={unlinkM.isPending}>
                <Unlink className="w-3 h-3" aria-hidden="true" />
              </Button>
            )}
          </div>
        )}
      </div>

      {linkQ.isLoading ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : link ? (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium">{link.sourceName}</span>
          <Badge variant="outline" className="text-xs">{link.sourceType}</Badge>
          {link.hipaaVerificationStatus && (
            <Badge
              variant={link.hipaaVerificationStatus === "active" ? "default" : link.hipaaVerificationStatus === "blocked" ? "destructive" : "secondary"}
              className="text-xs"
            >
              HIPAA: {link.hipaaVerificationStatus.replace("_", " ")}
            </Badge>
          )}
          {link.sourceUrl && <span className="text-muted-foreground truncate font-mono max-w-xs">{link.sourceUrl}</span>}
          {link.notes && <span className="text-muted-foreground italic">"{link.notes}"</span>}
        </div>
      ) : (
        <p className="text-muted-foreground">No knowledge source linked.{canManage ? ' Use "Link Source" to associate this control with a source.' : ''}</p>
      )}

      {/* Source picker dialog */}
      <Dialog open={showPicker} onOpenChange={v => { setShowPicker(v); }}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Link to Knowledge Source</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div>
              <label className="block text-xs font-medium mb-1">Select Knowledge Source</label>
              {sourcesQ.isLoading ? (
                <p className="text-muted-foreground text-xs">Loading sources…</p>
              ) : !sourcesQ.data?.length ? (
                <p className="text-muted-foreground text-xs">No knowledge sources found in this organisation.</p>
              ) : (
                <select
                  className="w-full border rounded px-2 py-1.5 text-sm"
                  value={selectedSourceId}
                  onChange={e => setSelectedSourceId(e.target.value)}
                >
                  <option value="">— select a source —</option>
                  {sourcesQ.data.map(s => (
                    <option key={s.id} value={s.id}>
                      {s.name}{s.url ? ` — ${s.url}` : ""} ({s.type}){s.hipaaVerificationStatus === "blocked" ? " ⚠ blocked" : ""}
                    </option>
                  ))}
                </select>
              )}
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Note (optional)</label>
              <input
                className="w-full border rounded px-2 py-1 text-sm"
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder="e.g. Source triggered this CSP finding on 2026-09-07"
                maxLength={500}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPicker(false)}>Cancel</Button>
            <Button
              onClick={() => linkM.mutate()}
              disabled={!selectedSourceId || linkM.isPending}
            >
              {linkM.isPending ? "Saving…" : "Link Source"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ── Standalone inline selector for use INSIDE create forms ───────────────────
// Renders as a compact "Link to Knowledge Source (optional)" dropdown + note.
// Parent form passes `onChange` to capture the selected sourceId.
export function SourceReferenceField({
  value,
  notes,
  onChangeSourceId,
  onChangeNotes,
}: {
  value: string;
  notes: string;
  onChangeSourceId: (id: string) => void;
  onChangeNotes: (n: string) => void;
}) {
  const authFetch = useHipaaFetch();
  const { data: sources, isLoading } = useQuery<AvailableSource[]>({
    queryKey: ["hipaa-available-sources"],
    queryFn: () => authFetch("/api/organization/compliance/hipaa/available-sources").then(r => r.json()),
    staleTime: 60_000,
  });

  return (
    <div className="rounded border p-3 space-y-2 bg-muted/30">
      <p className="text-xs font-semibold flex items-center gap-1">
        <Link2 className="w-3 h-3" aria-hidden="true" />
        Link to Knowledge Source <span className="text-muted-foreground font-normal">(optional)</span>
      </p>
      <div>
        <select
          className="w-full border rounded px-2 py-1.5 text-sm"
          value={value}
          onChange={e => onChangeSourceId(e.target.value)}
        >
          <option value="">— none —</option>
          {isLoading && <option disabled>Loading sources…</option>}
          {sources?.map(s => (
            <option key={s.id} value={s.id}>
              {s.name}{s.url ? ` — ${s.url}` : ""} ({s.type}){s.hipaaVerificationStatus === "blocked" ? " ⚠ blocked" : ""}
            </option>
          ))}
        </select>
        <p className="text-xs text-muted-foreground mt-0.5">Associate this control with the knowledge source that caused or relates to this finding.</p>
      </div>
      {value && (
        <div>
          <label className="block text-xs font-medium mb-1">Link note (optional)</label>
          <input
            className="w-full border rounded px-2 py-1 text-sm"
            value={notes}
            onChange={e => onChangeNotes(e.target.value)}
            placeholder="Brief reason for the link"
            maxLength={500}
          />
        </div>
      )}
    </div>
  );
}
